import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { getAdminTatlStory, updateAdminTatlStory } from "../../lib/adminApi";

const fromListToText = (arr) => (Array.isArray(arr) ? arr.filter(Boolean).join("\n") : "");
const toJsonList = (text) =>
  JSON.stringify(
    String(text || "")
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean)
  );

const TatlStoryEdit = () => {
  const { id } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [childName, setChildName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [location, setLocation] = useState("");
  const [impactDate, setImpactDate] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [quote, setQuote] = useState("");
  const [videoUrl, setVideoUrl] = useState("");

  const [highlights, setHighlights] = useState("");
  const [needs, setNeeds] = useState("");
  const [supportGiven, setSupportGiven] = useState("");
  const [outcomes, setOutcomes] = useState("");

  const [content, setContent] = useState("<p></p>");

  const [isPublished, setIsPublished] = useState(true);
  const [coverImage, setCoverImage] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [replaceGallery, setReplaceGallery] = useState(false);

  const editorRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const s = await getAdminTatlStory(id);

      setTitle(s.title || "");
      setChildName(s.child_name || "");
      setAge(s.age ? String(s.age) : "");
      setGender(s.gender || "");
      setLocation(s.location || "");
      setImpactDate(s.impact_date || "");
      setExcerpt(s.excerpt || "");
      setQuote(s.quote || "");
      setVideoUrl(s.video_url || "");

      setHighlights(fromListToText(s.highlights));
      setNeeds(fromListToText(s.needs));
      setSupportGiven(fromListToText(s.support_given));
      setOutcomes(fromListToText(s.outcomes));

      setContent(s.content || "<p></p>");
      setIsPublished(!!s.is_published);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  // ✅ keep editor synced
  useEffect(() => {
    if (!editorRef.current) return;
    const next = content || "<p></p>";
    if (editorRef.current.innerHTML !== next) {
      editorRef.current.innerHTML = next;
    }
  }, [content]);

  const applyCmd = (cmd, value = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(cmd, false, value);
    setContent(editorRef.current?.innerHTML || "<p></p>");
  };

  const canSave = useMemo(() => {
    return (
      title.trim() &&
      childName.trim() &&
      location.trim() &&
      impactDate.trim() &&
      excerpt.trim()
    );
  }, [title, childName, location, impactDate, excerpt]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSave || saving) return;

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("child_name", childName.trim());

      const ageVal = String(age || "").trim();
      if (ageVal) fd.append("age", ageVal);

      const genderVal = String(gender || "").trim();
      if (genderVal) fd.append("gender", genderVal);

      fd.append("location", location.trim());
      fd.append("impact_date", impactDate.trim());
      fd.append("excerpt", excerpt.trim());
      fd.append("content", (content || "<p></p>").trim() || "<p></p>");

      fd.append("highlights_json", toJsonList(highlights));
      fd.append("needs_json", toJsonList(needs));
      fd.append("support_given_json", toJsonList(supportGiven));
      fd.append("outcomes_json", toJsonList(outcomes));

      const quoteVal = String(quote || "").trim();
      if (quoteVal) fd.append("quote", quoteVal);

      const videoVal = String(videoUrl || "").trim();
      if (videoVal) fd.append("video_url", videoVal);

      fd.append("is_published", String(!!isPublished));
      fd.append("replace_gallery", String(!!replaceGallery));

      if (coverImage) fd.append("cover_image", coverImage);
      (gallery || []).forEach((f) => {
        if (f) fd.append("gallery_images", f);
      });

      await updateAdminTatlStory(id, fd);
      nav("/admin/tatl");
    } catch (err) {
      alert(err?.message || "Failed to update story");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Edit TATL1000 Story">
        <div className="h-32 rounded-2xl bg-stone-200 animate-pulse" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Edit TATL1000 Story" onRefresh={load}>
      <form onSubmit={onSubmit} className="space-y-6">
        <Card className="rounded-2xl border-stone-200">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-stone-600">Story Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>

              <div>
                <label className="text-sm text-stone-600">Impact Date (YYYY-MM-DD)</label>
                <input
                  value={impactDate}
                  onChange={(e) => setImpactDate(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>

              <div>
                <label className="text-sm text-stone-600">Child Name</label>
                <input
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-stone-600">Age</label>
                  <input
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                  />
                </div>
                <div>
                  <label className="text-sm text-stone-600">Gender</label>
                  <input
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-stone-600">Location</label>
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>

              <div>
                <label className="text-sm text-stone-600">Video URL (optional, YouTube)</label>
                <input
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-stone-600">Excerpt</label>
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300 min-h-[110px]"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-stone-600">Quote (optional)</label>
                <input
                  value={quote}
                  onChange={(e) => setQuote(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>

              <div>
                <label className="text-sm text-stone-600">Replace Cover Image (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCoverImage(e.target.files?.[0] || null)}
                  className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 bg-white"
                />
              </div>

              <div>
                <label className="text-sm text-stone-600">Add Gallery Images (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setGallery(Array.from(e.target.files || []))}
                  className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 bg-white"
                />

                <div className="mt-3 flex items-center gap-2">
                  <input
                    id="rep"
                    type="checkbox"
                    checked={replaceGallery}
                    onChange={(e) => setReplaceGallery(e.target.checked)}
                  />
                  <label htmlFor="rep" className="text-sm text-stone-700">
                    Replace existing gallery (instead of appending)
                  </label>
                </div>
              </div>

              <div className="md:col-span-2 flex items-center gap-2">
                <input
                  id="pub"
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                />
                <label htmlFor="pub" className="text-sm text-stone-700">
                  Published (visible to public)
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-stone-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-900 mb-3">Story Board (Rich Content)</h3>

            <div className="border rounded-xl overflow-hidden">
              <div className="p-2 border-b flex flex-wrap gap-2 bg-stone-50">
                <Button type="button" variant="outline" size="sm" onClick={() => applyCmd("bold")}>
                  Bold
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => applyCmd("italic")}>
                  Italic
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => applyCmd("underline")}>
                  Underline
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyCmd("insertUnorderedList")}
                >
                  • List
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyCmd("insertOrderedList")}
                >
                  1. List
                </Button>
              </div>

              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                className="min-h-[280px] p-4 outline-none"
                onInput={() => setContent(editorRef.current?.innerHTML || "<p></p>")}
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-stone-900 mb-3">Highlights (one per line)</h3>
              <textarea
                value={highlights}
                onChange={(e) => setHighlights(e.target.value)}
                className="w-full rounded-xl border border-stone-200 px-3 py-2 min-h-[160px] outline-none focus:ring-2 focus:ring-amber-300"
              />
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-stone-900 mb-3">Needs Identified (one per line)</h3>
              <textarea
                value={needs}
                onChange={(e) => setNeeds(e.target.value)}
                className="w-full rounded-xl border border-stone-200 px-3 py-2 min-h-[160px] outline-none focus:ring-2 focus:ring-amber-300"
              />
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-stone-900 mb-3">Support Given (one per line)</h3>
              <textarea
                value={supportGiven}
                onChange={(e) => setSupportGiven(e.target.value)}
                className="w-full rounded-xl border border-stone-200 px-3 py-2 min-h-[160px] outline-none focus:ring-2 focus:ring-amber-300"
              />
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-stone-900 mb-3">Outcomes (one per line)</h3>
              <textarea
                value={outcomes}
                onChange={(e) => setOutcomes(e.target.value)}
                className="w-full rounded-xl border border-stone-200 px-3 py-2 min-h-[160px] outline-none focus:ring-2 focus:ring-amber-300"
              />
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={!canSave || saving}
            className="bg-amber-700 hover:bg-amber-800 rounded-xl"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>

          <Button type="button" variant="outline" className="rounded-xl" onClick={() => nav("/admin/tatl")}>
            Cancel
          </Button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default TatlStoryEdit;
